import React, { useState, useEffect } from 'react'
import {
  CurrencyDollarIcon,
  WalletIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  PlusIcon,
  GlobeAltIcon,
  BanknotesIcon,
  FireIcon
} from '@heroicons/react/24/outline'
import { cryptoAPI, type CryptoWallet, type CryptoHolding, type CryptoStats } from '@/services/api'

const CryptoDashboard: React.FC = () => {
  const [wallets, setWallets] = useState<CryptoWallet[]>([])
  const [holdings, setHoldings] = useState<CryptoHolding[]>([])
  const [stats, setStats] = useState<CryptoStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedWallet, setSelectedWallet] = useState<CryptoWallet | null>(null)

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    wallet_type: '',
    currency_code: 'BTC',
    balance: '',
    public_address: '',
    exchange: '',
    is_active: true
  })

  useEffect(() => {
    const fetchCryptoData = async () => {
      try {
        setLoading(true)
        // Connect to REAL API endpoints - remove mock data usage
        const [walletsData, holdingsData, statsData] = await Promise.all([
          cryptoAPI.getWallets(),
          cryptoAPI.getHoldings(),
          cryptoAPI.getStats()
        ])
        setWallets(walletsData || [])
        setHoldings(holdingsData || [])
        setStats(statsData)
        setError(null)
      } catch (err: any) {
        console.error('Crypto API error:', err)
        setError(`Conectado ao backend real: ${err.message}`)
        // On error, set empty state
        setWallets([])
        setHoldings([])
        setStats(null)
      } finally {
        setLoading(false)
      }
    }

    fetchCryptoData()
  }, [])

  const formatCurrency = (value: number, currency = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency
    }).format(value)
  }

  const formatUSD = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value)
  }

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`
  }

  const getChangeColor = (change: number) => {
    return change >= 0 ? 'text-green-600' : 'text-red-600'
  }

  const resetForm = () => {
    setFormData({
      name: '',
      wallet_type: '',
      currency_code: 'BTC',
      balance: '',
      public_address: '',
      exchange: '',
      is_active: true
    })
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await cryptoAPI.createWallet({
        name: formData.name,
        wallet_type: formData.wallet_type as 'exchange' | 'hardware' | 'software',
        currency_code: formData.currency_code,
        balance: parseFloat(formData.balance),
        public_address: formData.public_address || undefined,
        exchange: formData.exchange || undefined,
        is_active: formData.is_active
      })
      resetForm()
      setShowCreateModal(false)
      // Refresh data
      const [walletsData, holdingsData, statsData] = await Promise.all([
        cryptoAPI.getWallets(),
        cryptoAPI.getHoldings(),
        cryptoAPI.getStats()
      ])
      setWallets(walletsData || [])
      setHoldings(holdingsData || [])
      setStats(statsData)
    } catch (err: any) {
      setError(`Erro ao criar carteira: ${err.message}`)
    }
  }

  const handleEdit = (wallet: CryptoWallet) => {
    setSelectedWallet(wallet)
    setFormData({
      name: wallet.name,
      wallet_type: wallet.wallet_type,
      currency_code: wallet.currency_code || 'BTC',
      balance: wallet.balance?.toString() || '',
      public_address: wallet.public_address || '',
      exchange: wallet.exchange || '',
      is_active: wallet.is_active !== false
    })
    setShowEditModal(true)
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedWallet) return
    try {
      await cryptoAPI.updateWallet(selectedWallet.id, {
        name: formData.name,
        wallet_type: formData.wallet_type as 'exchange' | 'hardware' | 'software',
        currency_code: formData.currency_code,
        balance: parseFloat(formData.balance),
        public_address: formData.public_address || undefined,
        exchange: formData.exchange || undefined,
        is_active: formData.is_active
      })
      resetForm()
      setShowEditModal(false)
      setSelectedWallet(null)
      // Refresh data
      const [walletsData, holdingsData, statsData] = await Promise.all([
        cryptoAPI.getWallets(),
        cryptoAPI.getHoldings(),
        cryptoAPI.getStats()
      ])
      setWallets(walletsData || [])
      setHoldings(holdingsData || [])
      setStats(statsData)
    } catch (err: any) {
      setError(`Erro ao atualizar carteira: ${err.message}`)
    }
  }

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta carteira?')) {
      try {
        await cryptoAPI.deleteWallet(id)
        // Refresh data
        const [walletsData, holdingsData, statsData] = await Promise.all([
          cryptoAPI.getWallets(),
          cryptoAPI.getHoldings(),
          cryptoAPI.getStats()
        ])
        setWallets(walletsData || [])
        setHoldings(holdingsData || [])
        setStats(statsData)
      } catch (err: any) {
        setError(`Erro ao excluir carteira: ${err.message}`)
      }
    }
  }

  const getWalletTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'exchange':
        return <GlobeAltIcon className="w-6 h-6" />
      case 'hardware':
        return <WalletIcon className="w-6 h-6" />
      case 'software':
        return <CurrencyDollarIcon className="w-6 h-6" />
      default:
        return <WalletIcon className="w-6 h-6" />
    }
  }


  const displayStats = stats || { total_value_usd: 0, total_value_brl: 0, total_change_24h: 0, total_change_percentage_24h: 0, top_performers: [], worst_performers: [] }
  const displayWallets = wallets
  const displayHoldings = holdings

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-gray-100 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6">
        <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar crypto</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CurrencyDollarIcon className="w-7 h-7 text-orange-500" />
            Portfolio Crypto
          </h1>
          <p className="text-gray-600">Gestão completa de criptomoedas e DeFi</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-yellow-600 flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <PlusIcon className="w-5 h-5" />
          Nova Carteira
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Portfolio Total</p>
              <p className="text-lg font-bold">{formatUSD(displayStats.total_value_usd)}</p>
              <p className="text-sm">{formatCurrency(displayStats.total_value_brl)}</p>
            </div>
            <BanknotesIcon className="w-8 h-8 text-orange-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Carteiras Ativas</p>
              <p className="text-2xl font-bold">{displayWallets.filter(w => w.is_active).length}</p>
            </div>
            <WalletIcon className="w-8 h-8 text-blue-200" />
          </div>
        </div>

        <div className={`rounded-xl p-6 text-white ${
          displayStats.total_change_percentage_24h >= 0
            ? 'bg-gradient-to-r from-green-500 to-green-600'
            : 'bg-gradient-to-r from-red-500 to-red-600'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm">Variação 24h</p>
              <p className="text-2xl font-bold">{formatPercent(displayStats.total_change_percentage_24h)}</p>
            </div>
            {displayStats.total_change_percentage_24h >= 0 ? (
              <ArrowTrendingUpIcon className="w-8 h-8 text-white/60" />
            ) : (
              <ArrowTrendingDownIcon className="w-8 h-8 text-white/60" />
            )}
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Holdings</p>
              <p className="text-2xl font-bold">{holdings.length}</p>
            </div>
            <FireIcon className="w-8 h-8 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Holdings Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Meus Holdings</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Moeda
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantidade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preço Atual
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  24h %
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Carteira
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayHoldings.map((holding) => {
                const wallet = displayWallets.find(w => w.id === holding.wallet_id)
                return (
                  <tr key={holding.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                            <span className="text-orange-600 font-bold text-sm">
                              {holding.symbol}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{holding.name}</div>
                          <div className="text-sm text-gray-500">{holding.symbol}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {holding.amount.toLocaleString('pt-BR', {
                        minimumFractionDigits: holding.amount < 1 ? 6 : 2,
                        maximumFractionDigits: holding.amount < 1 ? 6 : 2
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatUSD(holding.current_price_usd)}</div>
                      <div className="text-sm text-gray-500">{formatCurrency(holding.current_price_brl)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{formatUSD(holding.total_value_usd)}</div>
                      <div className="text-sm text-gray-500">{formatCurrency(holding.total_value_brl)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${getChangeColor(holding.price_change_24h)}`}>
                        {formatPercent(holding.price_change_24h)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {wallet?.name || 'Desconhecida'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Wallets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayWallets.map((wallet) => (
          <div key={wallet.id} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg text-orange-600">
                  {getWalletTypeIcon(wallet.wallet_type)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{wallet.name}</h3>
                  <span className="text-sm text-gray-500 capitalize">{wallet.wallet_type}</span>
                </div>
              </div>
              <div className={`w-3 h-3 rounded-full ${wallet.is_active ? 'bg-green-400' : 'bg-gray-300'}`}></div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Moeda:</span>
                <span className="font-medium">{wallet.currency_code || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Saldo:</span>
                <span className="font-bold text-green-600">{wallet.balance || 0} {wallet.currency_code || ''}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Valor USD:</span>
                <span className="font-bold">{formatUSD(wallet.balance_usd || 0)}</span>
              </div>
              {wallet.public_address && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Endereço:</span>
                  <span className="text-sm text-gray-500 font-mono">
                    {wallet.public_address.slice(0, 8)}...{wallet.public_address.slice(-8)}
                  </span>
                </div>
              )}
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => handleEdit(wallet)}
                  className="flex-1 bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                >
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(wallet.id)}
                  className="flex-1 bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {displayWallets.length === 0 && (
        <div className="text-center py-12">
          <WalletIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma carteira encontrada</h3>
          <p className="text-gray-500 mb-6">Conecte suas carteiras para começar</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700"
          >
            Conectar Primeira Carteira
          </button>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Nova Carteira Crypto</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Carteira
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Ex: Binance Principal"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo
                  </label>
                  <select
                    value={formData.wallet_type}
                    onChange={(e) => setFormData({...formData, wallet_type: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">Selecione</option>
                    <option value="exchange">Exchange</option>
                    <option value="hardware">Hardware</option>
                    <option value="software">Software</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Moeda
                  </label>
                  <select
                    value={formData.currency_code}
                    onChange={(e) => setFormData({...formData, currency_code: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="BTC">Bitcoin (BTC)</option>
                    <option value="ETH">Ethereum (ETH)</option>
                    <option value="ADA">Cardano (ADA)</option>
                    <option value="BNB">Binance Coin (BNB)</option>
                    <option value="SOL">Solana (SOL)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Saldo Inicial
                </label>
                <input
                  type="number"
                  step="0.00000001"
                  value={formData.balance}
                  onChange={(e) => setFormData({...formData, balance: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="0.00000000"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endereço Público (opcional)
                </label>
                <input
                  type="text"
                  value={formData.public_address}
                  onChange={(e) => setFormData({...formData, public_address: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Endereço da carteira..."
                />
              </div>

              {formData.wallet_type === 'exchange' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Exchange
                  </label>
                  <input
                    type="text"
                    value={formData.exchange}
                    onChange={(e) => setFormData({...formData, exchange: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Ex: Binance, Coinbase, etc."
                  />
                </div>
              )}

              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Carteira Ativa</span>
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Criar Carteira
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">Editar Carteira</h3>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Carteira
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Ex: Binance Principal"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo
                  </label>
                  <select
                    value={formData.wallet_type}
                    onChange={(e) => setFormData({...formData, wallet_type: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">Selecione</option>
                    <option value="exchange">Exchange</option>
                    <option value="hardware">Hardware</option>
                    <option value="software">Software</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Moeda
                  </label>
                  <select
                    value={formData.currency_code}
                    onChange={(e) => setFormData({...formData, currency_code: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="BTC">Bitcoin (BTC)</option>
                    <option value="ETH">Ethereum (ETH)</option>
                    <option value="ADA">Cardano (ADA)</option>
                    <option value="BNB">Binance Coin (BNB)</option>
                    <option value="SOL">Solana (SOL)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Saldo
                </label>
                <input
                  type="number"
                  step="0.00000001"
                  value={formData.balance}
                  onChange={(e) => setFormData({...formData, balance: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="0.00000000"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endereço Público (opcional)
                </label>
                <input
                  type="text"
                  value={formData.public_address}
                  onChange={(e) => setFormData({...formData, public_address: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Endereço da carteira..."
                />
              </div>

              {formData.wallet_type === 'exchange' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Exchange
                  </label>
                  <input
                    type="text"
                    value={formData.exchange}
                    onChange={(e) => setFormData({...formData, exchange: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Ex: Binance, Coinbase, etc."
                  />
                </div>
              )}

              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Carteira Ativa</span>
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Atualizar Carteira
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default CryptoDashboard